/**
 * The Movers News cell: `tag · headline · 07:02 · +3`, and a click that asks
 * for the rest. Pure half: `lib/news-cell.ts`.
 *
 * The island renders and reports, `TrackListCell`'s seam: the list a click
 * opens is drawn by the host, which knows where the server is. The click is
 * swallowed on mousedown and click alike, for that cell's reason — a row click
 * charts the symbol, and AG Grid selects on mousedown.
 */

import type { CSSProperties, MouseEvent } from "react";

import { NEWS_WINDOWS, newsParts, type NewsWindow } from "../lib/news-cell.js";

/** The host's half: open the window's headlines for `symbol`, under `anchor`. */
export type NewsOpen = (symbol: string, window: NewsWindow, anchor: DOMRect) => void;

export interface NewsCellProps {
  data?: Record<string, unknown>;
  colDef?: { field?: string; colId?: string };
  /** AG Grid's `context`, where `ScreenerGrid` puts `onNewsOpen`. */
  context?: { onNewsOpen?: NewsOpen; idField?: string };
}

export function NewsCell({ data, colDef, context }: NewsCellProps) {
  const field = colDef?.field ?? colDef?.colId ?? "";
  const parts = newsParts(data, field, Date.now());
  if (!parts) return null;
  const symbol = String(data?.[context?.idField ?? "symbol"] ?? "");
  const open = context?.onNewsOpen;
  const window = NEWS_WINDOWS[field];

  const click = (event: MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation();
    if (open && symbol && window) open(symbol, window, event.currentTarget.getBoundingClientRect());
  };

  return (
    <span
      style={{ ...CELL, ...(parts.stale ? MUTED : null), cursor: open ? "pointer" : undefined }}
      title={parts.title}
      onMouseDown={open ? (event) => event.stopPropagation() : undefined}
      onClick={open ? click : undefined}
    >
      {parts.tag && <span style={TAG}>{parts.tag}</span>}
      <span style={TITLE}>{parts.title}</span>
      {parts.time && <span style={TIME}>{parts.time}</span>}
      {parts.more > 0 && <span style={MORE}>+{parts.more}</span>}
    </span>
  );
}

const CELL: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: 4,
  minWidth: 0,
  overflow: "hidden",
};
const MUTED: CSSProperties = { opacity: 0.55 };
const TAG: CSSProperties = { flex: "0 0 auto", fontSize: 11, color: "var(--qf-text-muted)" };
const TITLE: CSSProperties = {
  flex: "1 1 auto",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const TIME: CSSProperties = { flex: "0 0 auto", fontSize: 11, color: "var(--qf-text-muted)" };
const MORE: CSSProperties = { flex: "0 0 auto", fontSize: 11, color: "var(--qf-accent)" };
